import { AlertTriangle, FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  IMPORT_STATUS_LABEL,
  type ImportRowStatus,
} from '@obraplan/core/domain/imports/table-mapping';
import {
  EMPLOYEE_IMPORT_COLUMNS,
  type EmployeeColumnMapping,
  type EmployeeImportField,
} from '@obraplan/core/domain/workforce/employee-import';
import type { EmployeeImportPreview } from '@obraplan/core/application/use-cases/workforce/employee-import-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';

const STATUS_TONE: Readonly<
  Record<ImportRowStatus, 'neutral' | 'ok' | 'info' | 'warn' | 'danger'>
> = {
  CREATE: 'ok',
  UPDATE: 'info',
  UNCHANGED: 'neutral',
  INVALID: 'danger',
  DUPLICATE: 'warn',
};

export interface EmployeeImportOptions {
  readonly updateExisting: boolean;
  readonly createMissingRoles: boolean;
  readonly createMissingTeams: boolean;
}

export interface EmployeeImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: EmployeeImportPreview | undefined;
  onFile: (file: File) => void;
  onRemap: (mapping: EmployeeColumnMapping, createMissingRoles: boolean) => void;
  onConfirm: (options: EmployeeImportOptions) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}

/**
 * Importar a equipe de uma planilha.
 *
 * Três momentos na mesma tela: escolher o arquivo, conferir o que foi entendido de cada
 * coluna e ver linha a linha o que vai acontecer. Nada é gravado até a confirmação — quem
 * importa vê antes quantas pessoas são novas, quantas seriam atualizadas e quais linhas
 * têm problema.
 *
 * As duas decisões que mudam o resultado ficam explícitas, e não escondidas num padrão:
 * **criar as funções** que a planilha traz e o catálogo não tem, e **criar as equipes**
 * citadas nela. Criar por conta própria transformaria um "Pedrero" digitado errado em
 * função permanente.
 */
export function EmployeeImportDialog({
  open,
  onOpenChange,
  preview,
  onFile,
  onRemap,
  onConfirm,
  isPending,
  errorMessage,
}: EmployeeImportDialogProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [createMissingRoles, setCreateMissingRoles] = useState(false);
  const [createMissingTeams, setCreateMissingTeams] = useState(true);

  const mapping = preview?.mapping ?? {};
  const missingRequired = EMPLOYEE_IMPORT_COLUMNS.filter(
    (column) => column.required && mapping[column.field] === undefined,
  );

  const changeMapping = (field: EmployeeImportField, header: string): void => {
    const next: EmployeeColumnMapping = { ...mapping };
    if (header === '') delete next[field];
    else next[field] = header;
    onRemap(next, createMissingRoles);
  };

  const changeCreateRoles = (value: boolean): void => {
    setCreateMissingRoles(value);
    onRemap(mapping, value);
  };

  const applicable =
    preview === undefined
      ? 0
      : preview.summary.create + (updateExisting ? preview.summary.update : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Importar funcionários</DialogTitle>
          <DialogDescription>
            Exporte a relação da folha ou do controle do escritório em CSV ou XLSX. Nome e função
            bastam; CPF e matrícula, quando vierem, são o que identifica cada pessoa nas próximas
            importações.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* ── 1. Arquivo ───────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
            <FileSpreadsheet className="size-5 text-(--color-content-muted)" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              {preview === undefined ? (
                <p className="text-sm text-(--color-content-muted)">
                  Nenhum arquivo escolhido. A primeira linha deve ter os títulos das colunas.
                </p>
              ) : (
                <p className="truncate text-sm">
                  <strong>{preview.fileName}</strong>{' '}
                  <span className="text-(--color-content-muted)">
                    · {preview.format}
                    {preview.delimiter === undefined
                      ? ''
                      : ` · separador "${preview.delimiter === '\t' ? 'tab' : preview.delimiter}"`}
                    {preview.sheetName === undefined ? '' : ` · ${preview.sheetName}`} ·{' '}
                    {preview.summary.total} linhas
                  </span>
                </p>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) onFile(file);
                event.target.value = '';
              }}
            />
            <Button type="button" variant="secondary" onClick={() => fileInput.current?.click()}>
              <Upload aria-hidden="true" />
              {preview === undefined ? 'Escolher arquivo' : 'Trocar arquivo'}
            </Button>
          </div>

          {preview !== undefined && (
            <>
              {/* ── 2. Colunas ─────────────────────────────────────────────── */}
              <div>
                <h3 className="mb-2 text-sm font-medium">Colunas do arquivo</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {EMPLOYEE_IMPORT_COLUMNS.map((column) => (
                    <Field
                      key={column.field}
                      label={column.label}
                      htmlFor={`map-emp-${column.field}`}
                      required={column.required}
                    >
                      <select
                        id={`map-emp-${column.field}`}
                        className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                        value={mapping[column.field] ?? ''}
                        onChange={(event) => changeMapping(column.field, event.target.value)}
                      >
                        <option value="">— não importar —</option>
                        {preview.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>

                {missingRequired.length > 0 && (
                  <p
                    role="alert"
                    className="mt-2 flex items-center gap-2 text-sm text-(--color-state-danger)"
                  >
                    <AlertTriangle className="size-4" aria-hidden="true" />
                    Indique qual coluna traz{' '}
                    {missingRequired.map((column) => column.label.toLowerCase()).join(' e ')}.
                  </p>
                )}
              </div>

              {/* ── 3. O que vai acontecer ─────────────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="ok">{preview.summary.create} novos</Badge>
                <Badge tone="info">{preview.summary.update} atualizam</Badge>
                {preview.summary.unchanged > 0 && (
                  <Badge tone="neutral">{preview.summary.unchanged} sem mudança</Badge>
                )}
                {preview.summary.duplicate > 0 && (
                  <Badge tone="warn">{preview.summary.duplicate} repetidos no arquivo</Badge>
                )}
                {preview.summary.invalid > 0 && (
                  <Badge tone="danger">{preview.summary.invalid} com erro</Badge>
                )}
              </div>

              {(preview.unknownRoles.length > 0 || preview.newTeams.length > 0) && (
                <div className="flex flex-col gap-1 rounded-md border border-(--color-border-subtle) p-3 text-sm">
                  {preview.unknownRoles.length > 0 && (
                    <p>
                      <span className="font-medium">Funções fora do catálogo:</span>{' '}
                      {preview.unknownRoles.join(', ')}
                    </p>
                  )}
                  {preview.newTeams.length > 0 && (
                    <p>
                      <span className="font-medium">Equipes novas:</span>{' '}
                      {preview.newTeams.join(', ')}
                    </p>
                  )}
                </div>
              )}

              <TableWrapper className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Linha</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Função</TableHead>
                      <TableHead>CPF</TableHead>
                      <TableHead>Equipe</TableHead>
                      <TableHead>Observação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.slice(0, 200).map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell className="tabular text-(--color-content-muted)">
                          {row.rowNumber}
                        </TableCell>
                        <TableCell>
                          <Badge tone={STATUS_TONE[row.status]}>
                            {IMPORT_STATUS_LABEL[row.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-56 truncate">{row.name || '—'}</TableCell>
                        <TableCell>{row.jobRoleLabel || '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{row.cpf ?? '—'}</TableCell>
                        <TableCell>{row.teamLabel || '—'}</TableCell>
                        <TableCell className="text-xs text-(--color-content-muted)">
                          {row.issues.map((issue) => issue.message).join(' ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableWrapper>

              {preview.rows.length > 200 && (
                <p className="text-xs text-(--color-content-muted)">
                  Mostrando as 200 primeiras linhas; a importação considera todas as{' '}
                  {preview.summary.total}.
                </p>
              )}

              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(event) => setUpdateExisting(event.target.checked)}
                  />
                  Atualizar quem já está cadastrado com o mesmo CPF ou matrícula
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={createMissingRoles}
                    onChange={(event) => changeCreateRoles(event.target.checked)}
                  />
                  Criar as funções que não existem no catálogo
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={createMissingTeams}
                    onChange={(event) => setCreateMissingTeams(event.target.checked)}
                  />
                  Criar as equipes citadas e vincular quem for delas
                </label>
              </div>
            </>
          )}

          {errorMessage !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={
              isPending || preview === undefined || applicable === 0 || missingRequired.length > 0
            }
            onClick={() => onConfirm({ updateExisting, createMissingRoles, createMissingTeams })}
          >
            {isPending ? 'Importando…' : `Importar ${String(applicable)} funcionários`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
